import type { AgentSnapshot } from "@remote-agents/shared";
import { buildExecutionView } from "../execution";
import { formatElapsed, formatRelative } from "../format";

type Props = {
  agent: AgentSnapshot;
  now: number;
  onReviewLastInstruction?: () => void;
};

export function StatusHero({ agent, now, onReviewLastInstruction }: Props) {
  const view = buildExecutionView(agent, now);

  return (
    <section className="card agent-hero" aria-live="polite">
      <div className="agent-hero__top">
        <span className={`status-pill status-pill--${agent.status}`}>
          {view.statusLabel}
        </span>
        {view.elapsedMs !== null && agent.runStartedAt ? (
          <span className="hero-stat" title="Time since this run started">
            <span className="hero-stat__label">Elapsed</span>
            <span className="hero-stat__value">
              {formatElapsed(agent.runStartedAt, now)}
            </span>
          </span>
        ) : null}
        {view.phase === "running" && view.toolCount > 0 ? (
          <span className="hero-stat" title="Tool calls observed this run">
            <span className="hero-stat__label">Tools</span>
            <span className="hero-stat__value">{view.toolCount}</span>
          </span>
        ) : null}
        {view.isStale ? (
          <span className="chip chip--warn" title="Run is active but the stream has gone quiet">
            Stale stream
          </span>
        ) : null}
      </div>

      <p className="agent-hero__headline">{view.headline}</p>

      {agent.summary && view.phase === "running" ? (
        <p className="muted agent-hero__summary">{agent.summary}</p>
      ) : null}

      {view.lastActions.length > 0 ? (
        <div className="tool-trail" aria-label="Recent tool activity">
          {view.lastActions.map((action, index) => (
            <span key={`${action}-${index}`} className="chip tool-trail__chip">
              {action}
            </span>
          ))}
        </div>
      ) : null}

      {agent.subagents && agent.subagents.length > 0 ? (
        <div className="agent-hero__subagents">
          <span className="hero-stat__label">
            {agent.subagents.filter((s) => s.status === "running").length > 0
              ? `Running ${agent.subagents.filter((s) => s.status === "running").length} parallel subagent${agent.subagents.filter((s) => s.status === "running").length === 1 ? "" : "s"}`
              : `${agent.subagents.length} parallel subagent${agent.subagents.length === 1 ? "" : "s"} finished`}
          </span>
          <div className="agent-hero__subagent-chips">
            {agent.subagents.map((s) => (
              <span
                key={s.name}
                className={`chip ${s.status === "running" ? "chip--warn" : "chip--queue"}`}
                title={
                  s.status === "running"
                    ? "Subagent running"
                    : "Subagent finished"
                }
              >
                {s.status === "running" ? "⧉ " : "✓ "}
                {s.name}
              </span>
            ))}
          </div>
        </div>
      ) : null}

      <p className="muted agent-hero__meta">
        {view.lastUpdateMs
          ? view.isStale
            ? `Last stream event ${formatRelative(view.lastUpdateMs, now)}`
            : `Last update ${formatRelative(view.lastUpdateMs, now)}`
          : `Snapshot ${formatRelative(agent.updatedAt, now)}`}
        {agent.queueLength > 0
          ? ` · ${agent.queueLength} instruction${agent.queueLength === 1 ? "" : "s"} queued`
          : ""}
        {agent.runId ? ` · run ${agent.runId.slice(0, 8)}` : ""}
      </p>

      {view.phase === "error" && onReviewLastInstruction ? (
        <div className="execution-error">
          <p className="form-error" role="alert">
            {agent.headline || "The last run ended with an error."}
          </p>
          <p className="muted">
            Review your last accepted instruction below before sending again. The
            dashboard will not resend it automatically.
          </p>
          <button
            type="button"
            className="btn btn--accent"
            onClick={onReviewLastInstruction}
          >
            Review last instruction
          </button>
        </div>
      ) : null}
    </section>
  );
}
